<script setup>
import { WButton, WIcon, WParagraph } from '@alice-health/wonderland-vue'
import { computed, ref } from 'vue'

import {
  HDrawer,
  HTab,
  HTabContent,
  HTabs,
  LoadingComponent
} from '@/components/shared/atoms'
import { CHANNEL_KINDS } from '@/models/channels'
import { useStore } from '@/wrappers/store'

import {
  CEmptyState,
  DChannelsTable,
  DChannelsTableAdministrative,
  DMemberCard,
  DStaffData
} from '../atoms'
import { CViewChat } from '../molecules'

const props = defineProps({
  staff: {
    type: Object,
    default: () => ({})
  },
  channels: {
    type: Array,
    default: () => []
  },
  show: {
    type: Boolean
  },
  loading: {
    type: Boolean,
    default: false
  },
  isAdministrative: {
    type: Boolean,
    default: false
  }
})

const { mapActions } = useStore()

const { getMember } = mapActions('member')
const { setMonitoredChannel } = mapActions('channel/dashboard')

const emit = defineEmits(['onClose', 'onChannelKindChange'])

const tab = ref('Chats')
const channel = ref({ type: Object, default: () => ({}) })
const displayChannelView = ref(false)
const memberInfos = ref({ type: Object, default: () => ({}) })

const hasChannels = computed(() => {
  return props.channels && props.channels.length
})

const channelId = computed(() => {
  return channel.value.id
})

const handleReceivedChannel = channelReceived => {
  channel.value = channelReceived
  displayChannelView.value = true
  member(channel.value.personId)
  setMonitoredChannel(channel.value.id)
}

const member = async personIdReceived => {
  memberInfos.value = await getMember({
    personId: personIdReceived
  })
}

const handleChangeTab = tab => {
  emit(
    'onChannelKindChange',
    tab === 'Chats' ? CHANNEL_KINDS.CHAT : CHANNEL_KINDS.CHANNEL
  )
}

const handleClose = () => {
  emit('onClose')
  displayChannelView.value = false
}

const returnToChannelsAndChats = () => {
  displayChannelView.value = false
}
</script>
<template>
  <HDrawer v-if="show" @on-close="handleClose">
    <div class="back_button_container">
      <div
        v-if="displayChannelView"
        class="back_button"
        @click="returnToChannelsAndChats()"
      >
        <WButton variant="tertiary" icon-button icon="icArrowLeft" />
        <WParagraph style="padding-top: 9px">Voltar</WParagraph>
      </div>
      <div v-else class="back_button_placeholder"></div>
    </div>
    <div class="drawer__main">
      <DStaffData :staff="staff" class="drawer__staff" />
      <div v-if="displayChannelView" class="drawer__tl_vision">
        <DMemberCard :member-infos="memberInfos" :channel-id="channelId" />
        <CViewChat :channel="channel" />
      </div>
      <div v-else>
        <HTabs v-model:activeTab="tab" @update:active-tab="handleChangeTab">
          <template #tabs>
            <HTab name="Chats"><WIcon icon="icChat" /> Chats</HTab>
            <HTab name="Canais"><WIcon icon="icHashtag" /> Canais</HTab>
          </template>
          <template v-if="hasChannels" #contents>
            <HTabContent name="Chats">
              <LoadingComponent :loading="loading">
                <DChannelsTable v-if="!isAdministrative" :channels="channels" />
                <DChannelsTableAdministrative
                  v-else
                  :channels="channels"
                  @send-channel="handleReceivedChannel"
                />
              </LoadingComponent>
            </HTabContent>
            <HTabContent name="Canais">
              <LoadingComponent :loading="loading">
                <DChannelsTable v-if="!isAdministrative" :channels="channels" />
                <DChannelsTableAdministrative
                  v-else
                  :channels="channels"
                  @send-channel="handleReceivedChannel"
                />
              </LoadingComponent>
            </HTabContent>
          </template>
        </HTabs>
      </div>

      <CEmptyState v-if="!hasChannels" message="Nenhum resultado encontrado" />
    </div>
  </HDrawer>
</template>
<style lang="scss" scoped>
.back_button {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-content: center;
  cursor: pointer;
}

.back_button_container {
  display: flex;
  flex-direction: column;
  height: 40px;
}

.back_button_placeholder {
  height: 50px;
}

.drawer {
  &__staff {
    margin-bottom: var(--gl-spacing-08);
  }
  &__tl_vision {
    display: flex;
    flex-direction: column;
    gap: var(--gl-spacing-08);
  }
}
</style>
